import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CampaignService } from "@gen/tracker/v1/campaign_connect";
import { CampaignStatus, type CampaignListItem } from "@gen/tracker/v1/campaign_pb";
import { DashboardService } from "@gen/tracker/v1/dashboard_connect";
import type { PnLPoint } from "@gen/tracker/v1/dashboard_pb";
import { DateRange, Pagination, SortOrder } from "@gen/tracker/v1/common_pb";
import { StatsSortOptions } from "@gen/tracker/v1/analytics_pb";
import { MetricFilter, MetricFilterOp } from "@gen/tracker/v1/reporting_pb";

import { createClient } from "@/lib/connect";
import { PAGE_MAX } from "@/lib/services/catalog";
import { campaignKeys } from "@/lib/services/campaign-service";

const campaigns = createClient(CampaignService);
const dashboard = createClient(DashboardService);

/** «Сегодня» refreshes on its own; a buyer leaves it open. */
export const TODAY_POLL_MS = 60_000;

/** How many campaigns each of «Лучшие» and «Худшие» shows (доска Today). */
export const LEADERS = 7;

const LIVE = [CampaignStatus.ACTIVE, CampaignStatus.PAUSED];

/**
 * The caller's own campaign ids, for «Мои» — the caller as the server knows
 * him (`mine`), never a sub the browser names. Disabled under «Все».
 */
export function useOwnCampaignIds(mine: boolean) {
  return useQuery({
    queryKey: [...campaignKeys.all, "today-own", mine] as const,
    enabled: mine,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      const res = await campaigns.listCampaigns({ mine: true, pagination: new Pagination({ limit: PAGE_MAX, offset: 0 }) });
      return res.items.flatMap((i) => (i.campaign ? [i.campaign.id] : []));
    },
  });
}

/**
 * The numbers by HOUR over [from, to] in `tz` — the one source of the strip,
 * «к вчера к 15:53» and the running chart (the same reader as every report).
 * Under «Мои» only the caller's campaigns; with none, no request at all.
 */
export function useHourlyTrend(from: string, to: string, tz: string, ids: string[] | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["dashboard", "hourly", from, to, tz, ids ?? null] as const,
    enabled,
    refetchInterval: TODAY_POLL_MS,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PnLPoint[]> =>
      (await dashboard.getPnLTrend({ dateRange: new DateRange({ from, to, timezone: tz }), hourly: true, campaignIds: ids ?? [] })).points,
  });
}

export interface TodayCampaigns {
  best: CampaignListItem[];
  worst: CampaignListItem[];
  losing: CampaignListItem[];
  noClicks: CampaignListItem[];
  /** Every campaign of the workspace outside the archive — 0 is the first run (доска TodayFirstRun). */
  total: number;
}

/**
 * The day's campaigns, ranked and flagged by the server over today: the
 * seven that made the most, the seven that lost the most, the ones losing
 * money (quick «Сливают») and the ones that spend without a click. Nothing is
 * sorted or judged in the browser.
 */
export function useTodayCampaigns(today: string, tz: string, mine: boolean) {
  return useQuery({
    queryKey: [...campaignKeys.all, "today", today, tz, mine] as const,
    refetchInterval: TODAY_POLL_MS,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<TodayCampaigns> => {
      const base = {
        statuses: LIVE,
        mine,
        statsRange: new DateRange({ from: today, to: today, timezone: tz }),
      };
      const ranked = (order: SortOrder, op: MetricFilterOp) =>
        campaigns.listCampaigns({
          ...base,
          pagination: new Pagination({ limit: LEADERS, offset: 0 }),
          metricFilters: [new MetricFilter({ metricKey: "profit", op, value: "0" })],
          sort: new StatsSortOptions({ metricKey: "profit", order }),
        });
      const flagged = (quick: string) =>
        campaigns.listCampaigns({
          ...base,
          quick,
          pagination: new Pagination({ limit: 5, offset: 0 }),
          sort: new StatsSortOptions({ metricKey: "profit", order: SortOrder.ASC }),
        });
      const [best, worst, losing, noClicks, all] = await Promise.all([
        ranked(SortOrder.DESC, MetricFilterOp.GT),
        ranked(SortOrder.ASC, MetricFilterOp.LT),
        flagged("losing"),
        flagged("no_clicks"),
        campaigns.listCampaigns({ statuses: [CampaignStatus.DRAFT, ...LIVE], pagination: new Pagination({ limit: 1, offset: 0 }) }),
      ]);
      return {
        best: best.items,
        worst: worst.items,
        losing: losing.items,
        noClicks: noClicks.items,
        total: all.pagination?.total ?? all.items.length,
      };
    },
  });
}
